import { useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthGate } from "@/components/auth/AuthGate";
import { UploadPage } from "@/pages/UploadPage";
import { MappingPage } from "@/pages/MappingPage";
import { KnownMappingsPage } from "@/pages/KnownMappingsPage";
import { AnalyticsPage } from "@/pages/AnalyticsPage";
import { DataQualityPage } from "@/pages/DataQualityPage";
import { SchemaBuilderPage } from "@/pages/SchemaBuilderPage";
import { SchemaBuilderEditPage } from "@/pages/SchemaBuilderEditPage";
import { useAuthToken } from "@/hooks/useAuthToken";
import { UploadProvider } from "@/hooks/useUpload";
import { trackPageView } from "@/services/telemetry";
import { featureFlags } from "./config/featureFlags";

/** Human-readable names for each route -- used in AppInsights page view telemetry. */
const PAGE_NAMES: Record<string, string> = {
  "/": "Upload",
  "/mapping": "Column Mapping Review",
  "/data-quality": "Data Quality",
  "/known-mappings": "Known Mappings",
  "/history": "Processing History",
  "/analytics": "Analytics",
  "/schema-builder": "Schema Builder",
};

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    const name = location.pathname.startsWith("/schema-builder")
      ? "Schema Builder Edit"
      : (PAGE_NAMES[location.pathname] ?? location.pathname);
    trackPageView(name, location.pathname, {
      search: location.search || undefined,
    });
  }, [location.pathname, location.search]);

  return null;
}

export function App() {
  // Register the token provider so API calls include the Bearer token.
  // In dev bypass mode this returns a static token.
  // In MSAL mode this acquires tokens silently from the cached session
  // (AuthGate guarantees the user is already authenticated by this point).
  useAuthToken();

  return (
    <AuthGate>
      <UploadProvider>
        <PageViewTracker />
        <Routes>
          <Route element={<AppLayout />}>
            <Route path="/" element={<UploadPage />} />
            <Route path="/mapping" element={<MappingPage />} />
            <Route path="/data-quality" element={<DataQualityPage />} />
            <Route path="/known-mappings" element={<KnownMappingsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            {featureFlags.schemaBuilder && (
              <>
                <Route path="/schema-builder" element={<SchemaBuilderPage />} />
                <Route path="/schema-builder/:schemaId" element={<SchemaBuilderEditPage />} />
              </>
            )}
          </Route>
        </Routes>
      </UploadProvider>
    </AuthGate>
  );
}
